import { inject } from '@angular/core';
import { ImageConversionService, NotificationService } from '@core/services';
import { GetPushNotificationSeen, GetUserId } from '@core/utils';
import { forkJoin, map, Observable, of } from 'rxjs';
import { CRMNewsPushNotificationEntity, CrmSurveyEntity, SurveyPushNotificationEntity } from '@core/entities';
import { plainToInstance } from 'class-transformer';
import { catchError } from 'rxjs/operators';
import { PermissionService } from '@auth';

export class UsePushNotification {
  private readonly _notificationService = inject(NotificationService);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _permissionService = inject(PermissionService);

  getSurveyNotifications(): Observable<SurveyPushNotificationEntity[]> {
    return this._notificationService.getSurveyNotifications(+GetUserId()).pipe(
      map((response) => {
        if (!response) return [];
        return response.map((item: any) => plainToInstance(SurveyPushNotificationEntity, item)).filter((item: SurveyPushNotificationEntity) => item.author !== +GetUserId());
      }),
    );
  }

  getCrmNewsNotifications(): Observable<any> {
    return this._notificationService.getCrmNewsNotifications().pipe(
      map((response) =>
        response
          .map((item: any) => {
            const transformedEntity = plainToInstance(CRMNewsPushNotificationEntity, item);

            if (transformedEntity.picture) {
              transformedEntity.imageUrl = this._imageConversionService.convertBase64ToSafeUrl(transformedEntity.picture);
            }

            if (transformedEntity.image && !transformedEntity.imageUrl) {
              transformedEntity.imageUrl = this._imageConversionService.convertBase64ToSafeUrl(transformedEntity.image);
            }
            return transformedEntity;
          })
          .filter((item: CRMNewsPushNotificationEntity) => item.authorId !== +GetUserId()),
      ),
      catchError(() => of(null)),
    );
  }

  getCrmSurveyNotifications(): Observable<CrmSurveyEntity[]> {
    return this._notificationService.getCrmSurveyNotifications().pipe(
      map((response) =>
        response
          .map((item: any) => {
            const transformedEntity = plainToInstance(CrmSurveyEntity, item);
            if (transformedEntity.picture) {
              transformedEntity.imageUrl = this._imageConversionService.convertBase64ToSafeUrl(transformedEntity.picture);
            }
            transformedEntity.createdAt = new Date(transformedEntity.createdAt.split('T')[0]).toISOString();
            return transformedEntity;
          })
          .filter((item: CrmSurveyEntity) => item.authorId !== +GetUserId()),
      ),
      catchError(() => of(null)),
    );
  }

  getAllNotifications(): Observable<any> {
    const seen = GetPushNotificationSeen();
    if (seen || this._permissionService.isGuest()) {
      return this.getSurveyNotifications().pipe(
        map((surveyNotifications) => ({
          surveyNotifications,
          crmNewsNotifications: null,
          crmSurveyNotifications: null,
        })),
      );
    } else {
      return forkJoin({
        surveyNotifications: this.getSurveyNotifications(),
        crmNewsNotifications: this.getCrmNewsNotifications(),
        crmSurveyNotifications: this.getCrmSurveyNotifications(),
      }).pipe(
        map((results) => ({
          surveyNotifications: results.surveyNotifications,
          crmNewsNotifications: results.crmNewsNotifications,
          crmSurveyNotifications: results.crmSurveyNotifications,
        })),
      );
    }
  }

  readCrmNewsNotifications(newsIds: number[]) {
    if (!newsIds || !newsIds.length) {
      throw new Error('News IDs are required');
    }
    return this._notificationService.readCrmNewsNotifications(newsIds);
  }

  readCrmSurveyNotifications(surveyIds: number[]) {
    if (!surveyIds || !surveyIds.length) {
      throw new Error('Survey IDs are required');
    }
    return this._notificationService.readCrmSurveyNotifications(surveyIds);
  }

  readLocalSurveyNotifications(surveyIds: number[]) {
    if (!surveyIds || !surveyIds.length) {
      throw new Error('Survey IDs are required');
    }

    const body = {
      id: surveyIds,
      loginId: +GetUserId(),
    };
    return this._notificationService.readSurveyNotifications(body);
  }
}
